import React, { useEffect } from 'react';
import { Redirect, Route, Switch, useHistory, useParams, useLocation } from 'react-router-dom';
import { Modal } from 'antd';
import { observer } from 'mobx-react';
import { configure } from 'mobx';

/* ===== STORES ===== */
import UserStore from 'stores/UserStore';
import UIStore from '@/stores/UIStore';
import QuoteStore from 'stores/QuoteStore';
import ComingSoonFeaturesStore from 'stores/ComingSoonFeaturesStore';

/* ===== LAYOUT ===== */
import PortalHeader from 'layouts/sections/PortalHeader';
import SideNav from 'layouts/sections/SideNav';
import Footer from 'layouts/sections/Footer';

/* ===== PAGES ===== */
import Home from 'pages/Home';
import FourOhFour from 'pages/404';
import SearchPage from 'pages/Search';
import Support from 'pages/Support';
import InviteYourColleagues from 'pages/InviteYourColleagues';

import { OrderDetail, OrderNew, Reorder, SalesOrders } from 'pages/Orders';

import { GetQuote, QuoteDetail, QuoteEdit, QuoteModificationsList, QuoteNew } from 'pages/Quote';
import QuotePdfOrderRedirect from '@/pages/Quote/QuotePdfOrderRedirect';

import { WelcomeToGetQuote } from 'pages/GetQuote';
import QuotesList from 'pages/Quote/QuotesList';

import { Parts, PartDetail, PartEdit, Cart, GroupDetail } from 'pages/Parts';

import Part3DViewer from 'pages/Part3DViewer';
import OrderNow from 'pages/PurchaseOrderSubmittion/OrderNow';
import OrderReview from 'pages/OrderReview';

import { InvoiceList, InvoiceDetails } from 'pages/Invoices';
import FormSubmissionResult from 'pages/FormSubmissionResult';
import FileConversionTool from 'pages/FileConversionTool';
import FileCreationgRequest from 'pages/FileCreationgRequest';
import SettingsRouter from './SettingsRouter';
import VerifyUser from '@/components/GetQuote/LoginOrRegisterModal/VerifyUser';
import QuoteCheckoutRedirect from '@/pages/Quote/QuoteCheckoutRedirect';
import ReorderV2 from '@/pages/Orders/NewReorder';

configure({ enforceActions: 'observed' });

/* ====================================================== */

interface Props {
    children?: React.ReactNode;
}

type RouteParams = {
    quote_id: string;
};

/* ===== REQUOTE HANDLER ===== */
const RequoteStartPage: React.FC = () => {
    const { quote_id } = useParams<RouteParams>();
    const history = useHistory();

    useEffect(() => {
        const startRequote = async () => {
            try {
                const response = await QuoteStore.requote(Number(quote_id));
                if (!response?.id) {
                    throw new Error('Invalid requote response');
                }
                history.replace(`/my-account/quotes/${response.id}/continue/`);
            } catch {
                Modal.error({
                    title: 'Error',
                    content: 'Not possible to start requote. Please try again later.',
                });
                history.replace('/my-account/quotes');
            }
        };

        startRequote();
    }, [quote_id, history]);

    return null;
};

/* ====================================================== */

const PrivateRouter: React.FC<Props> = observer(({ children }) => {
    const location = useLocation();

    const allowedRoutesForUnverified = [
        '/my-account',
        '/my-account/settings',
        '/my-account/settings/company-profile',
        '/my-account/support',
    ];

    const hideFooter =
        location.pathname === '/my-account/quotes/new-quote/' ||
        location.pathname.includes('/continue/') ||
        location.pathname.includes('/orders/checkout/') ||
        location.pathname.includes('/reorder');

    const routes = (
        <Switch>
            {/* HOME */}
            <Route exact path="/my-account" component={Home} />

            {/* ORDERS */}
            <Route exact path="/my-account/orders/:id" component={OrderDetail} />
            <Route exact path="/my-account/orders" component={SalesOrders} />
            <Route exact path="/my-account/orders/new/:id" component={OrderNew} />
            {/* <Route exact path="/my-account/orders/:id/reorder" component={Reorder} /> */}
            <Route exact path="/my-account/orders/:id/reorder" component={ReorderV2} />
            <Route
                exact
                path="/my-account/orders/checkout/:orderId/"
                render={props => <OrderReview {...props} mode="portal" />}
            />
            <Route exact path="/my-account/orders/reorder/success" component={FormSubmissionResult} />

            {/* QUOTES */}
            <Route path="/my-account/quotes/get-quote" component={GetQuote} />
            <Route exact path="/my-account/quotes" component={QuotesList} />
            <Route exact path="/my-account/quotes/new-quote" component={WelcomeToGetQuote} />
            {/* <Route exact path="/my-account/quotes/old-get-quote" component={QuoteNew} /> */}
            <Route exact path="/my-account/quotes/:id/continue/" component={WelcomeToGetQuote} />
            <Route exact path="/my-account/quotes/:id/pdf-order/" component={QuotePdfOrderRedirect} />
            <Route exact path="/my-account/quotes/:id" component={QuoteDetail} />
            <Route exact path="/my-account/quotes/:id/order" component={QuoteEdit} />
            <Route exact path="/my-account/quotes/:quote_uid/checkout/" component={QuoteCheckoutRedirect} />
            <Route exact path="/my-account/quotes/:id/modifications" component={QuoteModificationsList} />
            <Route exact path="/my-account/quotes/order/success" component={FormSubmissionResult} />
            <Route exact path="/my-account/quotes/:quote_number/order-now/" component={OrderNow} />

            {/* PARTS */}
            <Route exact path="/my-account/parts" component={Parts} />
            <Route exact path="/my-account/parts/cart" component={Cart} />
            <Route exact path="/my-account/parts/group/:id" component={GroupDetail} />
            <Route exact path="/my-account/parts/:id" component={PartDetail} />
            <Route exact path="/my-account/parts/edit" component={PartEdit} />
            <Route exact path="/my-account/part-viewer" component={Part3DViewer} />

            {/* INVOICES */}
            <Route exact path="/my-account/invoices" component={InvoiceList} />
            <Route exact path="/my-account/details-invoice/:id" component={InvoiceDetails} />

            {/* TOOLS */}
            {UserStore.user?.is_tester && (
                <Route exact path="/my-account/file-converter" component={FileConversionTool} />
            )}
            <Route exact path="/my-account/file-creation" component={FileCreationgRequest} />

            {/* MISC */}
            <Route exact path="/my-account/search" component={SearchPage} />
            <Route exact path="/my-account/support" component={Support} />
            <Route exact path="/my-account/invite-your-colleagues" component={InviteYourColleagues} />
            <Route path="/my-account/requote/start/:quote_id" component={RequoteStartPage} />

            <Redirect from="/my-account/order-requirements" to="/my-account/settings/order-requirements" />

            <SettingsRouter />
            <Route component={FourOhFour} />
        </Switch>
    );

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* ================= HEADER ================= */}
            <div style={{ flex: '0 0 auto' }}>
                <PortalHeader />
            </div>

            {/* ================= BODY ================= */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    minHeight: 0,
                }}
            >
                {/* SIDENAV */}
                <div style={{ flex: '0 0 auto' }}>
                    <SideNav />
                </div>

                {/* CONTENT */}
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            minHeight: 0,
                        }}
                    >
                        {!UserStore.isVerified ? (
                            <>
                                <VerifyUser onResendVerification={() => {}} onSuccess={() => {}} />
                            </>
                        ) : (
                            routes
                        )}
                    </div>
                </div>
            </div>

            {/* ================= FOOTER ================= */}
            {!hideFooter && (
                <div style={{ flex: '0 0 auto' }}>
                    <Footer />
                </div>
            )}

            {/* ================= GLOBAL MODAL ================= */}
            <Modal
                open={ComingSoonFeaturesStore.modalVisible}
                onCancel={() => ComingSoonFeaturesStore.setModalVisible(false)}
                footer={null}
                centered
                width={640}
                destroyOnClose
            >
                <h2 className="mb-4 font-weight-bold">{ComingSoonFeaturesStore.modalHeader}</h2>
                <h4 className="font-weight-light">{ComingSoonFeaturesStore.modalTextContent}</h4>
            </Modal>
        </div>
    );
});

export default PrivateRouter;
